const DESKTOP_QUERY = window.matchMedia('(min-width: 990px)');

const ATMOSPHERE_DISPLAY_TIME = 8000;
const ATMOSPHERE_FADE_OUT_TIME = 1100;
const ATMOSPHERE_FADE_IN_TIME = 1500;
const ATMOSPHERE_OVERLAP_TIME = 200;
const FIRST_IMAGE_DELAY = 0;
const FIRST_IMAGE_FADE_TIME = 2000;
const SHOWROOM_HOVER_DELAY = 90;
const INITIAL_TEXT_DELAY = 1050;
const INITIAL_TEXT_DURATION = 1050;
const INITIAL_TEXT_STAGGER = 120;
const INITIAL_CTA_DELAY = 2250;
const INITIAL_CTA_DURATION = 1000;
const INITIAL_SHOWROOM_EASING = 'cubic-bezier(0.16, 1, 0.3, 1)';
const ACTIVE_SUBMENU_START_PROPERTY = '--simedge-active-submenu-start';

let atmosphereSources = [];
let atmosphereOpen = false;
let atmosphereSession = 0;
let preparationSession = 0;
let currentAtmosphereUrl = '';
let lastShownAtmosphereUrl = '';
let preparedOpeningUrl = '';
let activeAtmosphereLayer = 'a';
let activeMainMenuTrigger = null;

const atmosphereTimers = new Map();
const imagePreloadCache = new Map();
const showroomResetters = new WeakMap();
const showroomOpeners = new WeakMap();
const showroomFallbackCleanups = new WeakMap();

function getHeaderComponent() {
  return (
    document.querySelector('#header-component') ||
    document.querySelector('header-component')
  );
}

function getHeaderMenu() {
  return document.querySelector('header-menu');
}

function getMegaMenuInteractionBridge(headerMenu = getHeaderMenu()) {
  return headerMenu?.querySelector('[data-simedge-mega-interaction-bridge]') || null;
}

function ensureMegaMenuDismissLayer() {
  const existingLayer = document.querySelector(
    '[data-simedge-mega-dismiss-layer]'
  );
  if (existingLayer instanceof HTMLElement) return existingLayer;

  const layer = document.createElement('div');
  layer.dataset.simedgeMegaDismissLayer = '';
  layer.hidden = true;
  layer.setAttribute('aria-hidden', 'true');
  Object.assign(layer.style, {
    position: 'fixed',
    insetInline: '0',
    top: '0',
    bottom: '0',
    zIndex: '2147483646',
    background: 'transparent',
    pointerEvents: 'auto',
  });
  document.body.appendChild(layer);
  return layer;
}

function updateMegaMenuDismissLayer(headerMenu = getHeaderMenu()) {
  const layer = ensureMegaMenuDismissLayer();
  if (!(layer instanceof HTMLElement)) return;

  const headerComponent = getHeaderComponent();
  const fullOpenHeight = Number.parseFloat(
    headerComponent?.style.getPropertyValue('--full-open-header-height') || '0'
  ) || 0;
  const headerTop = headerComponent?.getBoundingClientRect().top || 0;

  layer.style.top = `${Math.max(0, Math.round(headerTop + fullOpenHeight))}px`;
  layer.hidden = headerMenu?.getAttribute('aria-expanded') !== 'true';
}

function usesControlledMegaMenuHeight() {
  return document
    .querySelector('#MainContent[data-template]')
    ?.matches(
      '[data-template^="collection"], [data-template^="product"]'
    ) || false;
}

function usesHomepageMegaMenuBoundary() {
  return document
    .querySelector('#MainContent[data-template]')
    ?.matches('[data-template^="index"]') || false;
}

function getLogoMarquee() {
  return document.querySelector(
    '.shopify-section.logo-marquee-wrapper, .logo-marquee-wrapper'
  );
}

function getDocumentTop(element) {
  const rect = element.getBoundingClientRect();

  return rect.top + window.scrollY;
}

function getVisibleHeaderChrome(headerComponent) {
  if (headerComponent.hasAttribute('data-submenu-overlap-bottom-row')) {
    const topRow = headerComponent.querySelector('.header__row--top');

    if (topRow instanceof HTMLElement) return topRow;
  }

  return headerComponent;
}

function getMegaMenuPanelStart(headerComponent) {
  const visibleHeaderChrome = getVisibleHeaderChrome(headerComponent);

  return (
    visibleHeaderChrome.getBoundingClientRect().top +
    visibleHeaderChrome.offsetHeight
  );
}

function hideMegaMenuInteractionBridge(headerMenu = getHeaderMenu()) {
  const bridge = getMegaMenuInteractionBridge(headerMenu);
  if (!(bridge instanceof HTMLElement)) return;

  bridge.hidden = true;
  bridge.style.removeProperty('left');
  bridge.style.removeProperty('top');
  bridge.style.removeProperty('width');
  bridge.style.removeProperty('height');
}

function updateMegaMenuInteractionBridge(headerMenu = getHeaderMenu()) {
  if (!DESKTOP_QUERY.matches || !(headerMenu instanceof HTMLElement)) {
    hideMegaMenuInteractionBridge(headerMenu);
    return;
  }

  const headerComponent = getHeaderComponent();
  const activeTrigger = headerMenu.querySelector(
    '.menu-list__list-item:not([slot="overflow"]) > .menu-list__link[aria-expanded="true"]'
  );
  const activeNavigation = activeTrigger
    ?.closest('.menu-list__list-item')
    ?.querySelector('.mega-menu__list');
  const bridge = getMegaMenuInteractionBridge(headerMenu);

  if (
    !(headerComponent instanceof HTMLElement) ||
    !(activeNavigation instanceof HTMLElement) ||
    !(bridge instanceof HTMLElement)
  ) {
    hideMegaMenuInteractionBridge(headerMenu);
    return;
  }

  const hostRect = headerMenu.getBoundingClientRect();
  const navigationRect = activeNavigation.getBoundingClientRect();
  bridge.hidden = false;
  const offsetParentRect = bridge.offsetParent?.getBoundingClientRect();
  const panelStart = getMegaMenuPanelStart(headerComponent);

  if (!offsetParentRect) {
    hideMegaMenuInteractionBridge(headerMenu);
    return;
  }

  const overlap = 1;
  const top = hostRect.bottom - overlap;
  const bottom = panelStart + overlap;
  const left = Math.min(hostRect.left, navigationRect.left);
  const right = Math.max(hostRect.right, navigationRect.right);
  const width = right - left;
  const height = bottom - top;

  if (width <= 0 || height <= 0) {
    hideMegaMenuInteractionBridge(headerMenu);
    return;
  }

  bridge.style.left = `${left - offsetParentRect.left}px`;
  bridge.style.top = `${top - offsetParentRect.top}px`;
  bridge.style.width = `${width}px`;
  bridge.style.height = `${height}px`;
  bridge.hidden = false;
}

function getVisualViewportBottom() {
  const viewport = window.visualViewport;

  return viewport
    ? viewport.offsetTop + viewport.height
    : document.documentElement.clientHeight;
}

function getHomepageMegaMenuBoundary(panelStart) {
  if (!usesHomepageMegaMenuBoundary()) return null;

  const logoMarquee = getLogoMarquee();
  if (!(logoMarquee instanceof HTMLElement)) return null;

  const rect = logoMarquee.getBoundingClientRect();
  const styles = window.getComputedStyle(logoMarquee);
  const visible =
    logoMarquee.getClientRects().length > 0 &&
    rect.width > 0 &&
    rect.height > 0 &&
    styles.display !== 'none' &&
    styles.visibility !== 'hidden' &&
    styles.visibility !== 'collapse';

  return visible && Number.isFinite(rect.top) && rect.top > panelStart
    ? rect.top
    : null;
}

function updateMegaMenuAvailableHeight() {
  const headerComponent = getHeaderComponent();
  if (!headerComponent) return;

  const property = '--simedge-mega-available-height';

  if (!DESKTOP_QUERY.matches) {
    headerComponent.style.removeProperty(property);
    return;
  }

  const panelStart = getMegaMenuPanelStart(headerComponent);
  const viewportBottom = getVisualViewportBottom();
  const homepageBoundary = getHomepageMegaMenuBoundary(panelStart);
  const lowerBoundary = homepageBoundary === null
    ? viewportBottom
    : Math.min(viewportBottom, homepageBoundary);

  if (!Number.isFinite(panelStart) || !Number.isFinite(lowerBoundary)) {
    headerComponent.style.removeProperty(property);
    return;
  }

  const availableHeight = Math.max(
    0,
    Math.floor(lowerBoundary - panelStart)
  );
  const nextValue = `${availableHeight}px`;

  if (headerComponent.style.getPropertyValue(property) !== nextValue) {
    headerComponent.style.setProperty(property, nextValue);
  }
}

function updateMegaMenuHeight() {
  const headerComponent = getHeaderComponent();

  if (!headerComponent) {
    return;
  }

  if (!DESKTOP_QUERY.matches || usesControlledMegaMenuHeight()) {
    headerComponent.style.removeProperty(
      '--simedge-mega-height'
    );
    return;
  }

  const logoMarquee = getLogoMarquee();

  if (!logoMarquee) {
    headerComponent.style.removeProperty(
      '--simedge-mega-height'
    );
    return;
  }

  const headerBottom =
    getDocumentTop(headerComponent) +
    headerComponent.offsetHeight;

  const logoMarqueeTop =
    getDocumentTop(logoMarquee);

  const measuredHeight = Math.round(
    logoMarqueeTop - headerBottom
  );

  if (measuredHeight <= 0) {
    headerComponent.style.removeProperty(
      '--simedge-mega-height'
    );
    return;
  }

  headerComponent.style.setProperty(
    '--simedge-mega-height',
    `${measuredHeight}px`
  );
}

function syncOpenControlledMegaMenuHeight() {
  if (!DESKTOP_QUERY.matches || !usesControlledMegaMenuHeight()) return;

  const headerComponent = getHeaderComponent();
  if (!headerComponent) return;

  const activeTrigger = headerComponent.querySelector(
    '.menu-list__list-item:not([slot="overflow"]) > .menu-list__link[aria-expanded="true"]'
  );
  const activeSubmenu = activeTrigger
    ?.closest('.menu-list__list-item')
    ?.querySelector(':scope > .menu-list__submenu');

  if (!(activeSubmenu instanceof HTMLElement)) return;

  const submenuHeight = activeSubmenu.offsetHeight;
  if (submenuHeight <= 0) return;

  const visibleHeaderHeight = headerComponent.hasAttribute(
    'data-submenu-overlap-bottom-row'
  )
    ? headerComponent.querySelector('.header__row--top')?.offsetHeight || 0
    : headerComponent.offsetHeight;
  const submenuHeightValue = `${submenuHeight}px`;
  const fullOpenHeightValue = `${submenuHeight + visibleHeaderHeight}px`;

  if (
    headerComponent.style.getPropertyValue('--submenu-height') !==
    submenuHeightValue
  ) {
    headerComponent.style.setProperty('--submenu-height', submenuHeightValue);
  }

  if (
    headerComponent.style.getPropertyValue('--full-open-header-height') !==
    fullOpenHeightValue
  ) {
    headerComponent.style.setProperty(
      '--full-open-header-height',
      fullOpenHeightValue
    );
  }
}

let megaMenuHeightFrame = null;
let fullMegaMenuHeightUpdatePending = false;
let megaMenuHorizontalAnchorUpdatePending = false;

function scheduleMegaMenuHeightFrame() {
  if (megaMenuHeightFrame !== null) {
    cancelAnimationFrame(
      megaMenuHeightFrame
    );
  }

  megaMenuHeightFrame =
    requestAnimationFrame(() => {
      const updateLegacyHeight = fullMegaMenuHeightUpdatePending;
      const updateHorizontalAnchor = megaMenuHorizontalAnchorUpdatePending;

      megaMenuHeightFrame = null;
      fullMegaMenuHeightUpdatePending = false;
      megaMenuHorizontalAnchorUpdatePending = false;
      updateMegaMenuAvailableHeight();

      if (updateHorizontalAnchor) {
        updateActiveMegaMenuHorizontalAnchor();
      }

      updateMegaMenuInteractionBridge();

      if (updateLegacyHeight) {
        updateMegaMenuHeight();
        syncOpenControlledMegaMenuHeight();
      }

      updateMegaMenuDismissLayer();
    });
}

function scheduleMegaMenuHeightUpdate() {
  fullMegaMenuHeightUpdatePending = true;
  megaMenuHorizontalAnchorUpdatePending = true;
  scheduleMegaMenuHeightFrame();
}

function scheduleMegaMenuAvailableHeightUpdate() {
  scheduleMegaMenuHeightFrame();
}

function scheduleMegaMenuHorizontalAnchorUpdate() {
  megaMenuHorizontalAnchorUpdatePending = true;
  scheduleMegaMenuHeightFrame();
}


function getImageSources() {
  return [...document.querySelectorAll('[data-simedge-mega-source][data-src]')]
    .map((element) => element.dataset.src || '')
    .filter(Boolean);
}

function refreshAtmosphereSources() {
  atmosphereSources = [...new Set(getImageSources())];
  return atmosphereSources;
}

function chooseRandomImage(excludedUrls = []) {
  const sources = atmosphereSources.length
    ? atmosphereSources
    : refreshAtmosphereSources();

  if (!sources.length) return '';

  const excluded = new Set(excludedUrls.filter(Boolean));
  const available = sources.filter((url) => !excluded.has(url));
  const pool = available.length ? available : sources;

  return pool[Math.floor(Math.random() * pool.length)] || pool[0] || '';
}

function getNextAtmosphereImage(
  currentUrl,
  sources = refreshAtmosphereSources()
) {
  if (!sources.length) return '';

  const currentIndex = sources.indexOf(currentUrl);
  if (currentIndex < 0) return sources[0] || '';

  return sources[(currentIndex + 1) % sources.length] || '';
}

function preloadImage(url, priority = 'auto') {
  if (!url) return Promise.resolve(false);
  if (imagePreloadCache.has(url)) return imagePreloadCache.get(url);

  const promise = new Promise((resolve) => {
    const image = new Image();
    image.decoding = 'async';
    if ('fetchPriority' in image) image.fetchPriority = priority;
    image.onload = () => resolve(true);
    image.onerror = () => {
      imagePreloadCache.delete(url);
      resolve(false);
    };
    image.src = url;
  });

  imagePreloadCache.set(url, promise);
  return promise;
}

function preloadImagesInBackground(urls) {
  const uniqueUrls = [...new Set(urls)].filter(Boolean);
  if (!uniqueUrls.length) return;

  const load = () => uniqueUrls.forEach((url) => preloadImage(url, 'low'));
  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(load, { timeout: 1200 });
  } else {
    window.setTimeout(load, 350);
  }
}

function getShowroomImageUrls(root = document) {
  return [...root.querySelectorAll('[data-simedge-preview-source][data-image]')]
    .map((element) => element.dataset.image || '')
    .filter(Boolean);
}

function getInitialShowroomImageUrls(root = document) {
  return [...root.querySelectorAll('.mega-menu__grid')]
    .map((grid) => grid.querySelector('[data-simedge-preview-source][data-image]')?.dataset.image || '')
    .filter(Boolean);
}

function preloadShowroomImages(root = document) {
  if (!DESKTOP_QUERY.matches) return;

  const allUrls = [...new Set(getShowroomImageUrls(root))];
  const initialUrls = [...new Set(getInitialShowroomImageUrls(root))];

  initialUrls.forEach((url) => preloadImage(url, 'high'));
  const initialSet = new Set(initialUrls);
  preloadImagesInBackground(allUrls.filter((url) => !initialSet.has(url)));
}

function safeCssUrl(url) {
  return `url("${url.replace(/"/g, '\\"')}")`;
}

function setAtmosphereLayer(layer, url) {
  const headerComponent = getHeaderComponent();
  if (!headerComponent || !url) return;

  headerComponent.style.setProperty(
    `--simedge-mega-atmosphere-${layer}`,
    safeCssUrl(url)
  );
}

function showAtmosphereLayer(layer) {
  const headerComponent = getHeaderComponent();
  if (!headerComponent) return;

  headerComponent.classList.toggle('simedge-atmosphere-show-a', layer === 'a');
  headerComponent.classList.toggle('simedge-atmosphere-show-b', layer === 'b');
  activeAtmosphereLayer = layer;
}

function hideAtmosphereLayers() {
  getHeaderComponent()?.classList.remove(
    'simedge-atmosphere-show-a',
    'simedge-atmosphere-show-b'
  );
}

function waitForNextFrame(session) {
  return new Promise((resolve) => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => resolve(
        atmosphereOpen && session === atmosphereSession
      ));
    });
  });
}

function waitForAtmosphere(ms, session) {
  return new Promise((resolve) => {
    const id = window.setTimeout(() => {
      atmosphereTimers.delete(id);
      resolve(atmosphereOpen && session === atmosphereSession);
    }, ms);

    atmosphereTimers.set(id, resolve);
  });
}

function clearAtmosphereTimers() {
  atmosphereTimers.forEach((resolve, id) => {
    window.clearTimeout(id);
    resolve(false);
  });
  atmosphereTimers.clear();
}

function preloadNextAtmosphereImage(currentUrl, session) {
  if (!atmosphereOpen || session !== atmosphereSession) return;

  const nextUrl = getNextAtmosphereImage(currentUrl);
  if (!nextUrl || nextUrl === currentUrl) return;

  preloadImage(nextUrl, 'low');
}

async function prepareRandomOpeningImage(excludedUrls = []) {
  const thisPreparation = ++preparationSession;
  refreshAtmosphereSources();

  const selected = chooseRandomImage(excludedUrls);
  if (!selected) {
    preparedOpeningUrl = '';
    return false;
  }

  const loaded = await preloadImage(selected, 'high');
  if (!loaded || thisPreparation !== preparationSession || atmosphereOpen) {
    return false;
  }

  preparedOpeningUrl = selected;

  return true;
}

async function transitionToAtmosphere(nextUrl, session) {
  if (!nextUrl || nextUrl === currentAtmosphereUrl) return false;

  const loaded = await preloadImage(nextUrl);
  if (!loaded || !atmosphereOpen || session !== atmosphereSession) return false;

  const headerComponent = getHeaderComponent();
  if (!headerComponent) return false;

  const currentLayer = activeAtmosphereLayer;
  const nextLayer = currentLayer === 'a' ? 'b' : 'a';
  setAtmosphereLayer(nextLayer, nextUrl);

  const transitionClasses = [
    'simedge-atmosphere-fade-out-a',
    'simedge-atmosphere-fade-out-b',
    'simedge-atmosphere-fade-in-a',
    'simedge-atmosphere-fade-in-b',
  ];

  headerComponent.classList.remove(...transitionClasses);
  headerComponent.classList.add(
    `simedge-atmosphere-fade-out-${currentLayer}`,
    `simedge-atmosphere-fade-in-${nextLayer}`
  );

  if (!(await waitForNextFrame(session))) return false;

  headerComponent.classList.remove(
    currentLayer === 'a' ? 'simedge-atmosphere-show-a' : 'simedge-atmosphere-show-b'
  );

  if (!(await waitForAtmosphere(
    Math.max(0, ATMOSPHERE_FADE_OUT_TIME - ATMOSPHERE_OVERLAP_TIME),
    session
  ))) return false;

  headerComponent.classList.add(
    nextLayer === 'a' ? 'simedge-atmosphere-show-a' : 'simedge-atmosphere-show-b'
  );

  activeAtmosphereLayer = nextLayer;
  currentAtmosphereUrl = nextUrl;
  lastShownAtmosphereUrl = nextUrl;

  const completed = await waitForAtmosphere(ATMOSPHERE_FADE_IN_TIME, session);
  headerComponent.classList.remove(...transitionClasses);
  if (completed) preloadNextAtmosphereImage(currentAtmosphereUrl, session);
  return completed;
}

async function runAtmosphereCycle(session) {
  preloadNextAtmosphereImage(currentAtmosphereUrl, session);

  while (atmosphereOpen && session === atmosphereSession) {
    if (!(await waitForAtmosphere(ATMOSPHERE_DISPLAY_TIME, session))) return;

    const nextUrl = getNextAtmosphereImage(currentAtmosphereUrl);
    if (!nextUrl) return;

    const completed = await transitionToAtmosphere(nextUrl, session);
    if (!completed) return;
  }
}

async function openAtmosphereCycle() {
  if (atmosphereOpen || !DESKTOP_QUERY.matches) return;

  atmosphereOpen = true;
  const session = ++atmosphereSession;
  clearAtmosphereTimers();
  refreshAtmosphereSources();

  let openingUrl = preparedOpeningUrl;
  preparedOpeningUrl = '';
  preparationSession += 1;

  if (!openingUrl || !atmosphereSources.includes(openingUrl)) {
    openingUrl = chooseRandomImage([lastShownAtmosphereUrl]);
  }

  if (!openingUrl) {
    atmosphereOpen = false;
    return;
  }

  const loaded = await preloadImage(openingUrl, 'high');
  if (!loaded || !atmosphereOpen || session !== atmosphereSession) return;

  if (FIRST_IMAGE_DELAY > 0) {
    if (!(await waitForAtmosphere(FIRST_IMAGE_DELAY, session))) return;
  }

  const headerComponent = getHeaderComponent();
  if (!headerComponent) return;

  setAtmosphereLayer('a', openingUrl);
  setAtmosphereLayer('b', openingUrl);
  activeAtmosphereLayer = 'a';
  currentAtmosphereUrl = openingUrl;
  lastShownAtmosphereUrl = openingUrl;

  /*
    Första bilden börjar vakna direkt ur svart, men använder
    en långsammare och mer filmisk fade än de vanliga bytena.
  */
  headerComponent.classList.add('simedge-atmosphere-first-opening');

  if (!(await waitForNextFrame(session))) {
    headerComponent.classList.remove('simedge-atmosphere-first-opening');
    return;
  }

  showAtmosphereLayer('a');

  if (!(await waitForAtmosphere(FIRST_IMAGE_FADE_TIME, session))) {
    headerComponent.classList.remove('simedge-atmosphere-first-opening');
    return;
  }

  headerComponent.classList.remove('simedge-atmosphere-first-opening');
  runAtmosphereCycle(session);
}

function closeAtmosphereCycle() {
  if (!atmosphereOpen && preparedOpeningUrl) return;

  atmosphereOpen = false;
  atmosphereSession += 1;
  preparationSession += 1;
  clearAtmosphereTimers();

  const headerComponent = getHeaderComponent();
  headerComponent?.classList.remove('simedge-atmosphere-first-opening');

  hideAtmosphereLayers();
  activeAtmosphereLayer = 'a';
  currentAtmosphereUrl = '';

  /*
    Menyn är nu helt återställd. En ny slumpmässig öppningsbild
    förladdas, men inget bildspel eller visuellt bildbyte körs
    medan menyn är stängd.
  */
  prepareRandomOpeningImage([lastShownAtmosphereUrl]);
}

function normalizePath(url) {
  try {
    const parsedUrl = new URL(
      url,
      window.location.origin
    );

    return (
      parsedUrl.pathname.replace(/\/+$/, '') || '/'
    );
  } catch (error) {
    return '';
  }
}

function initProductPreview(grid) {
  if (
    !DESKTOP_QUERY.matches ||
    !(grid instanceof HTMLElement) ||
    grid.dataset.simedgePreviewReady === 'true'
  ) {
    return;
  }

  const preview = grid.querySelector(
    '[data-simedge-product-preview]'
  );
  const isBrandPreview =
    preview instanceof HTMLElement &&
    (preview.dataset.mode === 'brands' ||
      preview.classList.contains('simedge-mega__product-preview--brands'));
  const brandWatermarkLayer = grid
    .closest('.menu-list__submenu')
    ?.querySelector('[data-simedge-brand-watermark-layer]');
  const watermarks = brandWatermarkLayer instanceof HTMLElement
    ? [...brandWatermarkLayer.querySelectorAll('[data-simedge-brand-watermark]')]
    : [];

  const layers = [
    ...grid.querySelectorAll('[data-simedge-showroom-layer]'),
  ].map((layer, layerIndex) => ({
    root: layer,
    watermark: watermarks[layerIndex] || null,
    mediaLink: layer.querySelector('[data-simedge-product-preview-media-link]'),
    image: layer.querySelector('[data-simedge-product-preview-image]'),
    label: layer.querySelector('[data-simedge-product-preview-label]'),
    title: layer.querySelector('[data-simedge-product-preview-title]'),
    description: layer.querySelector('[data-simedge-product-preview-description]'),
    link: layer.querySelector('[data-simedge-product-preview-link]'),
  }));

  if (
    !(preview instanceof HTMLElement) ||
    layers.length !== 2 ||
    (isBrandPreview && !(brandWatermarkLayer instanceof HTMLElement)) ||
    layers.some((layer) =>
      !(layer.root instanceof HTMLElement) ||
      !(layer.mediaLink instanceof HTMLAnchorElement) ||
      !(layer.image instanceof HTMLImageElement) ||
      (isBrandPreview &&
        !(layer.watermark instanceof HTMLImageElement)) ||
      !(layer.label instanceof HTMLElement) ||
      !(layer.title instanceof HTMLElement) ||
      !(layer.description instanceof HTMLElement) ||
      !(layer.link instanceof HTMLAnchorElement)
    )
  ) {
    return;
  }

  const previewSources = [
    ...grid.querySelectorAll('[data-simedge-preview-source]'),
  ]
    .map((element) => ({
      path: normalizePath(element.dataset.url || ''),
      url: element.dataset.url || '',
      handle: element.dataset.handle || '',
      title: element.dataset.title || '',
      label: element.dataset.label || '',
      description: element.dataset.description || '',
      image: element.dataset.image || '',
      imageAlt:
        element.dataset.imageAlt ||
        element.dataset.title || '',
    }))
    .filter(
      (source) =>
        source.path &&
        source.url &&
        source.title &&
        source.image
    );

  if (previewSources.length === 0) {
    preview.hidden = true;
    return;
  }

  const sourceByPath = new Map(
    previewSources.map((source) => [source.path, source])
  );

  const menuLinks = [
    ...grid.querySelectorAll(
      '.mega-menu__list .mega-menu__link[href]'
    ),
  ];

  const reducedMotionQuery = window.matchMedia(
    '(prefers-reduced-motion: reduce)'
  );

  const previewStyle = window.getComputedStyle(preview);
  const hoverIntent = Number.parseFloat(
    previewStyle.getPropertyValue('--simedge-showroom-hover-intent')
  ) || SHOWROOM_HOVER_DELAY;

  let currentPath = '';
  let activeLayerIndex = 0;
  let previewRequestId = 0;
  let hoverTimer = null;
  let activeMenuLink = null;
  let initialEntrancePending = true;
  let brandEntranceAnimations = [];
  let brandEntrancePaintFrame = 0;
  let brandEntranceStartFrame = 0;

  function setActiveMenuLink(link) {
    if (activeMenuLink === link) return;

    activeMenuLink?.classList.remove('is-simedge-active');
    activeMenuLink = link instanceof HTMLElement ? link : null;
    activeMenuLink?.classList.add('is-simedge-active');
  }

  function clearHoverTimer() {
    window.clearTimeout(hoverTimer);
    hoverTimer = null;
  }

  function renderLayer(layer, source) {
    const mediaLabelPrefix = layer.mediaLink.dataset.labelPrefix || 'View';

    layer.mediaLink.href = source.url;
    layer.mediaLink.setAttribute(
      'aria-label',
      `${mediaLabelPrefix} ${source.title}`
    );

    layer.image.src = source.image;
    layer.image.alt = source.imageAlt;
    layer.root.dataset.brandHandle = source.handle;

    if (isBrandPreview) {
      layer.watermark.src = source.image;
    }

    layer.label.textContent = source.label;
    layer.label.hidden = !source.label;

    layer.title.textContent = source.title;

    layer.description.textContent = source.description;
    layer.description.hidden = !source.description;

    layer.link.classList.remove('is-simedge-cta-ready');
    layer.link.href = source.url;
    layer.link.setAttribute(
      'aria-label',
      `${layer.link.textContent.trim()}: ${source.title}`
    );
  }

  async function prepareLayer(layer, source) {
    renderLayer(layer, source);

    if (typeof layer.image.decode === 'function') {
      try {
        await layer.image.decode();
      } catch (error) {
        // The preload already succeeded; decode can reject during teardown.
      }
    }

    if (
      isBrandPreview &&
      typeof layer.watermark.decode === 'function'
    ) {
      try {
        await layer.watermark.decode();
      } catch (error) {
        // The watermark reuses the already preloaded primary logo asset.
      }
    }

    return (
      layer.image.complete &&
      layer.image.naturalWidth > 0 &&
      (!isBrandPreview ||
        (layer.watermark.complete && layer.watermark.naturalWidth > 0))
    );
  }

  function canAnimateShowroom() {
    return (
      DESKTOP_QUERY.matches &&
      !reducedMotionQuery.matches &&
      !isBrandPreview
    );
  }

  function animateInitialShowroom(layer) {
    if (!canAnimateShowroom()) return;

    preview.classList.add('is-simedge-initial-opening');

    const textItems = [
      layer.label,
      layer.title,
      layer.description,
    ];

    textItems.forEach((element, index) => {
      if (
        !(element instanceof HTMLElement) ||
        element.hidden
      ) {
        return;
      }

      element.getAnimations().forEach((animation) => {
        animation.cancel();
      });

      element.animate(
        [
          {
            opacity: 0,
            transform: 'translateY(40px)',
          },
          {
            opacity: 1,
            transform: 'translateY(0)',
          },
        ],
        {
          duration: INITIAL_TEXT_DURATION,
          delay: INITIAL_TEXT_DELAY + index * INITIAL_TEXT_STAGGER,
          easing: INITIAL_SHOWROOM_EASING,
          fill: 'both',
        }
      );
    });

    const link = layer.link;
    link.getAnimations().forEach((animation) => {
      animation.cancel();
    });
    link.classList.remove('is-simedge-cta-ready');

    const ctaAnimation = link.animate(
      [
        {
          opacity: 0,
          transform: 'translateX(-70px)',
        },
        {
          opacity: 1,
          transform: 'translateX(0)',
        },
      ],
      {
        duration: INITIAL_CTA_DURATION,
        delay: INITIAL_CTA_DELAY,
        easing: INITIAL_SHOWROOM_EASING,
        fill: 'both',
      }
    );

    ctaAnimation.finished
      .then(() => {
        if (link.isConnected) {
          link.classList.add('is-simedge-cta-ready');
          preview.classList.remove('is-simedge-initial-opening');
        }
      })
      .catch(() => {});
  }

  function clearInitialOpeningState() {
    preview.classList.remove('is-simedge-initial-opening');
  }

  function clearBrandEntranceState() {
    preview.classList.remove('is-simedge-brand-opening');
    brandWatermarkLayer?.classList.remove('is-simedge-brand-opening');
  }

  function cancelBrandEntrance() {
    window.cancelAnimationFrame(brandEntrancePaintFrame);
    window.cancelAnimationFrame(brandEntranceStartFrame);
    brandEntrancePaintFrame = 0;
    brandEntranceStartFrame = 0;

    const animations = brandEntranceAnimations;
    brandEntranceAnimations = [];
    clearBrandEntranceState();
    animations.forEach((animation) => animation.cancel());
  }

  function finishBrandEntrance(animations) {
    if (brandEntranceAnimations !== animations) return;

    clearBrandEntranceState();
    animations.forEach((animation) => animation.cancel());
    brandEntranceAnimations = [];
  }

  function animateInitialBrand(layer) {
    if (
      !DESKTOP_QUERY.matches ||
      !isBrandPreview ||
      reducedMotionQuery.matches
    ) {
      cancelBrandEntrance();
      return;
    }

    cancelBrandEntrance();
    preview.classList.add('is-simedge-brand-opening');
    brandWatermarkLayer.classList.add('is-simedge-brand-opening');

    brandEntrancePaintFrame = window.requestAnimationFrame(() => {
      brandEntrancePaintFrame = 0;
      brandEntranceStartFrame = window.requestAnimationFrame(() => {
        brandEntranceStartFrame = 0;

        if (
          !preview.classList.contains('is-simedge-brand-opening') ||
          !brandWatermarkLayer.classList.contains('is-simedge-brand-opening')
        ) {
          return;
        }

        const easing = 'cubic-bezier(0.4, 0, 0.2, 1)';
        const animationDefinitions = [
          {
            element: layer.watermark,
            keyframes: [
              {
                opacity: 0,
                transform: 'scale(1.035)',
              },
              {
                opacity: 0.1,
                transform: 'scale(1)',
              },
            ],
            options: { duration: 1100, delay: 0 },
          },
          {
            element: layer.image,
            keyframes: [
              { opacity: 0, transform: 'translateY(8px)' },
              { opacity: 1, transform: 'translateY(0)' },
            ],
            options: { duration: 750, delay: 180 },
          },
          {
            element: layer.label,
            keyframes: [
              { opacity: 0, transform: 'translateY(5px)' },
              { opacity: 1, transform: 'translateY(0)' },
            ],
            options: { duration: 500, delay: 380 },
          },
          {
            element: layer.description,
            keyframes: [
              { opacity: 0, transform: 'translateY(5px)' },
              { opacity: 1, transform: 'translateY(0)' },
            ],
            options: { duration: 500, delay: 430 },
          },
          {
            element: layer.link,
            keyframes: [
              { opacity: 0, transform: 'translateY(4px)' },
              { opacity: 1, transform: 'translateY(0)' },
            ],
            options: { duration: 450, delay: 540 },
          },
        ];

        const animations = animationDefinitions
          .filter(
            ({ element }) =>
              element instanceof HTMLElement && !element.hidden
          )
          .map(({ element, keyframes, options }) => {
            element.getAnimations().forEach((animation) => animation.cancel());

            return element.animate(keyframes, {
              ...options,
              easing,
              fill: 'both',
            });
          });

        brandEntranceAnimations = animations;
        Promise.allSettled(
          animations.map((animation) => animation.finished)
        ).then(() => finishBrandEntrance(animations));
      });
    });
  }

  function settleLayers(index = activeLayerIndex) {
    layers.forEach((layer, layerIndex) => {
      const active = layerIndex === index;
      layer.root.classList.toggle('is-simedge-active', active);
      layer.root.classList.remove(
        'is-simedge-entering',
        'is-simedge-leaving',
        'is-simedge-text-active'
      );
      layer.root.setAttribute('aria-hidden', active ? 'false' : 'true');
      layer.mediaLink.tabIndex = active ? 0 : -1;
      layer.link.tabIndex = active ? 0 : -1;

      if (isBrandPreview) {
        layer.watermark.classList.toggle('is-simedge-active', active);
      }
    });

    activeLayerIndex = index;
  }

  function getVisibleLayerIndex() {
    const activeIndex = layers.findIndex((layer) =>
      layer.root.classList.contains('is-simedge-active')
    );

    if (activeIndex !== -1) return activeIndex;

    const enteringIndex = layers.findIndex((layer) =>
      layer.root.classList.contains('is-simedge-entering')
    );

    return enteringIndex !== -1 ? enteringIndex : activeLayerIndex;
  }

  function clearLayerAnimations() {
    layers.forEach((layer) => {
      layer.root.getAnimations({ subtree: true }).forEach((animation) => {
        animation.cancel();
      });
      layer.link.classList.remove('is-simedge-cta-ready');
    });
  }

  function stabilizeLayers() {
    cancelBrandEntrance();
    clearInitialOpeningState();
    preview.classList.remove('is-simedge-choreographing');
    settleLayers(getVisibleLayerIndex());
    clearLayerAnimations();
  }

  function clearFallbackAnimationStyles(element) {
    if (!(element instanceof HTMLElement)) return;

    element.getAnimations().forEach((animation) => animation.cancel());
    [
      'animation',
      'opacity',
      'transform',
      'clip-path',
      '-webkit-clip-path',
    ].forEach((property) => element.style.removeProperty(property));
  }

  function enterNativeFallback() {
    clearHoverTimer();
    previewRequestId += 1;
    initialEntrancePending = true;
    currentPath = '';
    activeLayerIndex = 0;

    cancelBrandEntrance();
    clearInitialOpeningState();
    preview.classList.remove(
      'is-simedge-visible',
      'is-simedge-choreographing',
      'is-simedge-switching'
    );
    preview.setAttribute('aria-hidden', 'true');
    preview.setAttribute('inert', '');
    setActiveMenuLink(null);

    brandWatermarkLayer?.classList.remove('is-simedge-brand-opening');
    brandWatermarkLayer
      ?.getAnimations({ subtree: true })
      .forEach((animation) => animation.cancel());

    layers.forEach((layer) => {
      layer.root
        .getAnimations({ subtree: true })
        .forEach((animation) => animation.cancel());
      layer.root.classList.remove(
        'is-simedge-active',
        'is-simedge-entering',
        'is-simedge-leaving',
        'is-simedge-text-active'
      );
      layer.root.setAttribute('aria-hidden', 'true');
      layer.mediaLink.tabIndex = -1;
      layer.link.tabIndex = -1;
      layer.link.classList.remove('is-simedge-cta-ready');

      if (isBrandPreview) {
        layer.watermark.classList.remove('is-simedge-active');
      }

      [
        layer.watermark,
        layer.image,
        layer.label,
        layer.title,
        layer.description,
        layer.link,
      ].forEach(clearFallbackAnimationStyles);
    });
  }

  function cancelTransition() {
    previewRequestId += 1;

    /*
      Latest wins: cancel unfinished intro work and keep the currently
      active buffer fully visible before the next request is prepared.
    */
    stabilizeLayers();
  }

  async function showPreview(source, immediate = false) {
    if (!DESKTOP_QUERY.matches || !source) return;
    if (source.path === currentPath && !immediate) return;

    const requestId = ++previewRequestId;

    if (!immediate) {
      stabilizeLayers();
    }

    const imageLoaded = await preloadImage(
      source.image,
      immediate ? 'high' : 'auto'
    );

    if (
      !DESKTOP_QUERY.matches ||
      !imageLoaded ||
      requestId !== previewRequestId
    ) {
      return;
    }

    if (immediate) {
      preview.classList.remove('is-simedge-choreographing');
      renderLayer(layers[0], source);
      currentPath = source.path;
      settleLayers(0);
      preview.classList.add('is-simedge-visible');
      return;
    }

    const nextLayerIndex = activeLayerIndex === 0 ? 1 : 0;
    const nextLayer = layers[nextLayerIndex];
    const layerReady = await prepareLayer(nextLayer, source);

    if (
      !DESKTOP_QUERY.matches ||
      !layerReady ||
      requestId !== previewRequestId
    ) return;

    /*
      The inactive buffer is populated and decoded while the current buffer
      stays visible. Switching both roles synchronously makes the category
      change atomic in the next rendered frame.
    */
    settleLayers(nextLayerIndex);
    currentPath = source.path;
  }

  function schedulePreview(source) {
    clearHoverTimer();

    hoverTimer = window.setTimeout(() => {
      hoverTimer = null;
      showPreview(source);
    }, hoverIntent);
  }

  const firstMatchingLink = menuLinks.find((link) =>
    sourceByPath.has(normalizePath(link.href))
  );

  const initialSource = firstMatchingLink
    ? sourceByPath.get(normalizePath(firstMatchingLink.href))
    : previewSources[0];

  setActiveMenuLink(firstMatchingLink || null);
  showPreview(initialSource, true);

  function resetPreview() {
    if (!DESKTOP_QUERY.matches) {
      enterNativeFallback();
      return;
    }

    preview.removeAttribute('aria-hidden');
    preview.removeAttribute('inert');
    clearHoverTimer();
    initialEntrancePending = true;
    cancelTransition();
    currentPath = '';
    setActiveMenuLink(firstMatchingLink || null);
    showPreview(initialSource, true);
  }

  showroomResetters.set(grid, resetPreview);
  showroomFallbackCleanups.set(grid, enterNativeFallback);
  showroomOpeners.set(grid, () => {
    if (!initialEntrancePending || !DESKTOP_QUERY.matches) return;

    initialEntrancePending = false;
    const visibleLayer = layers[getVisibleLayerIndex()];

    if (isBrandPreview) {
      animateInitialBrand(visibleLayer);
      return;
    }

    animateInitialShowroom(visibleLayer);
  });

  menuLinks.forEach((link) => {
    const source = sourceByPath.get(normalizePath(link.href));

    link.addEventListener('pointerenter', () => {
      if (!DESKTOP_QUERY.matches) return;

      setActiveMenuLink(link);

      if (!source) {
        clearHoverTimer();
        return;
      }

      if (isBrandPreview && source.path !== currentPath) {
        cancelBrandEntrance();
      }

      schedulePreview(source);
    });

    link.addEventListener('pointerleave', clearHoverTimer);

    link.addEventListener('focus', () => {
      if (!DESKTOP_QUERY.matches) return;

      clearHoverTimer();
      setActiveMenuLink(link);

      if (!source) return;

      showPreview(source);
    });
  });

  grid.dataset.simedgePreviewReady = 'true';
}

function initProductPreviews(root = document) {
  if (!DESKTOP_QUERY.matches) return;

  root
    .querySelectorAll('.mega-menu__grid')
    .forEach(initProductPreview);
}

function resetProductPreviews(root = document) {
  root
    .querySelectorAll('.mega-menu__grid')
    .forEach((grid) => {
      showroomResetters.get(grid)?.();
    });
}


function setActiveMainMenuTrigger(trigger) {
  if (activeMainMenuTrigger === trigger) return;

  activeMainMenuTrigger?.classList.remove('is-simedge-main-active');
  activeMainMenuTrigger = trigger instanceof HTMLElement ? trigger : null;
  activeMainMenuTrigger?.classList.add('is-simedge-main-active');
}

function getVisibleMainMenuTrigger(event) {
  if (!(event.target instanceof Element)) return null;
  if (event.target.closest('.menu-list__submenu, .mega-menu')) return null;

  const trigger = event.target.closest(
    '.menu-list__list-item > .menu-list__link[aria-haspopup="true"]'
  );

  if (!(trigger instanceof HTMLElement)) return null;
  if (!event.composedPath().includes(trigger)) return null;

  const bounds = trigger.getBoundingClientRect();
  if (bounds.width <= 0 || bounds.height <= 0) return null;

  if (
    typeof PointerEvent !== 'undefined' &&
    event instanceof PointerEvent &&
    (event.clientX < bounds.left ||
      event.clientX > bounds.right ||
      event.clientY < bounds.top ||
      event.clientY > bounds.bottom)
  ) {
    return null;
  }

  return trigger;
}

function enterProductPreviewFallback(root = document) {
  root
    .querySelectorAll('.mega-menu__grid')
    .forEach((grid) => {
      showroomFallbackCleanups.get(grid)?.();
    });
}

function getTopLevelMainMenuEntries(headerMenu) {
  return [...headerMenu.querySelectorAll(
    '.menu-list__list-item:not([slot="overflow"])'
  )]
    .map((item) => ({
      item,
      trigger: item.querySelector(
        ':scope > .menu-list__link[aria-haspopup="true"]'
      ),
      panel: item.querySelector(':scope > .menu-list__submenu'),
    }))
    .filter(
      ({ item, trigger, panel }) =>
        item instanceof HTMLElement &&
        trigger instanceof HTMLElement &&
        panel instanceof HTMLElement
    );
}

function getNextActiveSubmenuStart(
  currentSubmenuStart,
  headingInlineStart,
  submenuLabelInlineStart
) {
  return (
    currentSubmenuStart + headingInlineStart - submenuLabelInlineStart
  );
}

function getViewportInlineStart(element, contentBox = false) {
  const rect = element.getBoundingClientRect();
  const styles = window.getComputedStyle(element);
  const padding = contentBox
    ? Number.parseFloat(styles.paddingInlineStart) || 0
    : 0;

  if (styles.direction === 'rtl') {
    return document.documentElement.clientWidth - rect.right + padding;
  }

  return rect.left + padding;
}

function clearActiveMegaMenuHorizontalAnchor(headerComponent) {
  headerComponent.style.removeProperty(ACTIVE_SUBMENU_START_PROPERTY);
  delete headerComponent.dataset.simedgeActiveSubmenuAnchor;
}

function updateActiveMegaMenuHorizontalAnchor(headerMenu = getHeaderMenu()) {
  const headerComponent = getHeaderComponent();

  if (
    !DESKTOP_QUERY.matches ||
    !(headerMenu instanceof HTMLElement) ||
    !(headerComponent instanceof HTMLElement)
  ) {
    if (headerComponent instanceof HTMLElement) {
      clearActiveMegaMenuHorizontalAnchor(headerComponent);
    }
    return;
  }

  const activeEntry = getTopLevelMainMenuEntries(headerMenu).find(
    ({ trigger }) => trigger.getAttribute('aria-expanded') === 'true'
  );
  const heading = activeEntry?.trigger.querySelector(
    ':scope > .menu-list__link-title'
  );
  const submenuInner = activeEntry?.panel.querySelector(
    ':scope > .menu-list__submenu-inner'
  );
  const submenuLabel = activeEntry?.panel.querySelector(
    '.mega-menu__list .mega-menu__link .wrap-text'
  );

  if (!activeEntry) {
    clearActiveMegaMenuHorizontalAnchor(headerComponent);
    return;
  }

  if (
    !(heading instanceof HTMLElement) ||
    !(submenuInner instanceof HTMLElement) ||
    !(submenuLabel instanceof HTMLElement)
  ) {
    return;
  }

  const headingRect = heading.getBoundingClientRect();
  const submenuLabelRect = submenuLabel.getBoundingClientRect();

  if (headingRect.width <= 0 || submenuLabelRect.width <= 0) return;

  const currentSubmenuStart = Number.parseFloat(
    window.getComputedStyle(submenuInner).paddingInlineStart
  ) || 0;
  const nextSubmenuStart = getNextActiveSubmenuStart(
    currentSubmenuStart,
    getViewportInlineStart(heading, true),
    getViewportInlineStart(submenuLabel)
  );

  if (!Number.isFinite(nextSubmenuStart) || nextSubmenuStart < 0) return;

  const nextValue = `${Math.round(nextSubmenuStart * 1000) / 1000}px`;

  if (
    headerComponent.style.getPropertyValue(ACTIVE_SUBMENU_START_PROPERTY) !==
    nextValue
  ) {
    headerComponent.style.setProperty(
      ACTIVE_SUBMENU_START_PROPERTY,
      nextValue
    );
  }

  headerComponent.dataset.simedgeActiveSubmenuAnchor = '';
}

function updateTopLevelMainMenuEntry(entry, active) {
  entry.panel.setAttribute('aria-hidden', active ? 'false' : 'true');
  entry.panel.toggleAttribute('inert', !active);

  if (active) {
    entry.panel.dataset.active = '';
    return;
  }

  delete entry.panel.dataset.active;
  entry.item.removeAttribute('open');
  entry.item
    .querySelectorAll(':scope > details[open]')
    .forEach((details) => details.removeAttribute('open'));
}

function setExclusiveMainMenuPanel(headerMenu, requestedTrigger) {
  const entries = getTopLevelMainMenuEntries(headerMenu);
  const activeEntry = entries.find(
    ({ trigger }) => trigger === requestedTrigger
  ) || null;

  entries
    .filter((entry) => entry !== activeEntry)
    .forEach((entry) => updateTopLevelMainMenuEntry(entry, false));

  if (activeEntry) {
    updateTopLevelMainMenuEntry(activeEntry, true);
  }

  setActiveMainMenuTrigger(activeEntry?.trigger || null);
  updateActiveMegaMenuHorizontalAnchor(headerMenu);
  updateMegaMenuInteractionBridge(headerMenu);
  updateMegaMenuDismissLayer(headerMenu);
}

function syncExclusiveMainMenuPanels(headerMenu, preferredTrigger = null) {
  const entries = getTopLevelMainMenuEntries(headerMenu);
  const expandedEntries = entries.filter(
    ({ trigger }) => trigger.getAttribute('aria-expanded') === 'true'
  );
  const activeEntry = expandedEntries.find(
    ({ trigger }) => trigger === preferredTrigger
  ) || expandedEntries.find(
    ({ trigger }) => trigger === activeMainMenuTrigger
  ) || expandedEntries[0] || null;

  setExclusiveMainMenuPanel(headerMenu, activeEntry?.trigger || null);
}

function closeMainMenu(headerMenu, event, force = false) {
  if (typeof headerMenu.closeMenu === 'function') {
    headerMenu.closeMenu({ force });
  } else if (event && typeof headerMenu.deactivate === 'function') {
    headerMenu.deactivate(event);
  }

  syncExclusiveMainMenuPanels(headerMenu);

  // The native Horizon controller owns the open state and header height.
  // If it deliberately kept the menu open, the SimEdge layer must not hide
  // only the panel and leave the header underlay in a mismatched state.
  if (headerMenu.getAttribute('aria-expanded') === 'true') return false;

  resetProductPreviews(headerMenu);
  closeAtmosphereCycle();
  return true;
}

function requestMainMenuClose(headerMenu) {
  if (headerMenu.getAttribute('aria-expanded') !== 'true') return;
  closeMainMenu(headerMenu, null, true);
}

function start() {
  const headerMenu = getHeaderMenu();
  if (!headerMenu) return;

  scheduleMegaMenuHeightUpdate();
  window.addEventListener('load', scheduleMegaMenuHeightUpdate, { once: true });
  window.addEventListener('resize', scheduleMegaMenuHeightUpdate, { passive: true });
  document.fonts?.ready.then(scheduleMegaMenuHorizontalAnchorUpdate);
  window.addEventListener(
    'scroll',
    scheduleMegaMenuAvailableHeightUpdate,
    { passive: true }
  );
  window.visualViewport?.addEventListener(
    'resize',
    scheduleMegaMenuHorizontalAnchorUpdate,
    { passive: true }
  );
  window.visualViewport?.addEventListener(
    'scroll',
    scheduleMegaMenuAvailableHeightUpdate,
    { passive: true }
  );

  const logoMarquee = getLogoMarquee();
  const headerComponent = getHeaderComponent();
  const dismissLayer = ensureMegaMenuDismissLayer();

  dismissLayer?.addEventListener('pointerenter', () => {
    requestMainMenuClose(headerMenu);
  });

  if ('ResizeObserver' in window) {
    const observer = new ResizeObserver(scheduleMegaMenuHeightUpdate);
    if (logoMarquee) observer.observe(logoMarquee);
    if (headerComponent) observer.observe(headerComponent);
  }

  preloadShowroomImages();
  refreshAtmosphereSources();
  prepareRandomOpeningImage();
  syncExclusiveMainMenuPanels(headerMenu);

  const panelStateObserver = new MutationObserver((mutations) => {
    const preferredTrigger = [...mutations]
      .reverse()
      .map(({ target }) => target)
      .find(
        (target) =>
          target instanceof HTMLElement &&
          target.matches(
            '.menu-list__list-item > .menu-list__link[aria-haspopup="true"][aria-expanded="true"]'
          )
      );

    syncExclusiveMainMenuPanels(headerMenu, preferredTrigger || null);
  });

  panelStateObserver.observe(headerMenu, {
    subtree: true,
    attributes: true,
    attributeFilter: ['aria-expanded'],
  });

  headerMenu.addEventListener('pointerover', (event) => {
    const trigger = getVisibleMainMenuTrigger(event);
    const insideMegaMenu =
      event.target instanceof Element &&
      event.target.closest('.menu-list__submenu, .mega-menu');

    if (trigger instanceof HTMLElement) {
      setExclusiveMainMenuPanel(headerMenu, trigger);
      const grid = trigger
        .closest('.menu-list__list-item')
        ?.querySelector('.mega-menu__grid');

      if (grid instanceof HTMLElement) {
        showroomOpeners.get(grid)?.();
      }
    }

    if (trigger || insideMegaMenu) openAtmosphereCycle();
  }, true);

  headerMenu.addEventListener('focusin', (event) => {
    const trigger = getVisibleMainMenuTrigger(event);

    if (trigger instanceof HTMLElement) {
      setExclusiveMainMenuPanel(headerMenu, trigger);
    }

    const grid = trigger
      ?.closest('.menu-list__list-item')
      ?.querySelector('.mega-menu__grid');

    if (grid instanceof HTMLElement) {
      showroomOpeners.get(grid)?.();
    }
  });

  headerMenu.addEventListener('pointerleave', (event) => {
    if (
      event.relatedTarget instanceof Node &&
      headerMenu.contains(event.relatedTarget)
    ) return;

    closeMainMenu(headerMenu, event);
  });

  document.addEventListener('pointerover', (event) => {
    if (headerMenu.getAttribute('aria-expanded') !== 'true') return;
    if (!(event.target instanceof Node) || headerMenu.contains(event.target)) return;

    requestMainMenuClose(headerMenu);
  }, true);

  window.addEventListener('blur', () => requestMainMenuClose(headerMenu));
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') {
      requestMainMenuClose(headerMenu);
    }
  });

  const mutationObserver = new MutationObserver(() => {
    initProductPreviews();
    refreshAtmosphereSources();

    if (!atmosphereOpen && !preparedOpeningUrl) {
      prepareRandomOpeningImage([lastShownAtmosphereUrl]);
    }
  });

  mutationObserver.observe(headerMenu, { subtree: true, childList: true });

  DESKTOP_QUERY.addEventListener('change', () => {
    closeAtmosphereCycle();

    if (DESKTOP_QUERY.matches) {
      preloadShowroomImages();
      prepareRandomOpeningImage([lastShownAtmosphereUrl]);
      initProductPreviews();
      resetProductPreviews();
    } else {
      enterProductPreviewFallback();
    }

    scheduleMegaMenuHeightUpdate();
  });

  document.addEventListener('shopify:section:load', (event) => {
    closeAtmosphereCycle();
    initProductPreviews(event.target);
    preloadShowroomImages(event.target);
    refreshAtmosphereSources();
    prepareRandomOpeningImage([lastShownAtmosphereUrl]);
    scheduleMegaMenuHeightUpdate();
  });

  initProductPreviews();
}

if (document.readyState === 'loading') {
  document.addEventListener(
    'DOMContentLoaded',
    start,
    {
      once: true,
    }
  );
} else {
  start();
}
